import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {clientActionsApi} from 'api';
import {Button, PredefinedNotifierType} from 'components';
import {useApiData} from 'hooks';
import {SessionScreenshotSubject} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {PreSessionStep} from 'types';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;

type Props = {
    sessionId: string;
    participantId: string;
    step: PreSessionStep;
    skipButton?: React.ReactNode;
};

export const ScreenshotStep: React.FC<Props> = ({sessionId, participantId, step, skipButton}) => {
    const {t} = useTranslation();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [streamError, setStreamError] = useState<string | null>(null);
    const [starting, setStarting] = useState(false);
    const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);

    const subject = useMemo(() => step.screenshot?.subject, [step]);

    const {send: submitScreenshot, loading: submitting} = useApiData(
        clientActionsApi.submitPreSessionScreenshot,
        {notificationType: PredefinedNotifierType.SaveError}
    );

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
    }, []);

    const requestStream = useCallback(async () => {
        setStreamError(null);
        // The permission prompt can sit open for a while; without this the client clicks again and a
        // second track is opened, leaking the first.
        setStarting(true);
        try {
            const mediaStream = subject === SessionScreenshotSubject.LocalScreen
                ? await navigator.mediaDevices.getDisplayMedia({video: true})
                : await navigator.mediaDevices.getUserMedia({video: true});
            streamRef.current = mediaStream;
            setStream(mediaStream);
        } catch (e: any) {
            setStreamError(e?.message ?? 'Permission denied');
        } finally {
            setStarting(false);
        }
    }, [subject]);

    useEffect(() => {
        if (!videoRef.current) {
            return;
        }
        videoRef.current.srcObject = stream;
    }, [stream]);

    // Release the camera/screen track on unmount regardless of which state we left off in.
    useEffect(() => () => stopStream(), [stopStream]);

    const handleCapture = useCallback(() => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) {
            return;
        }
        const width = video.videoWidth;
        const height = video.videoHeight;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        ctx.drawImage(video, 0, 0, width, height);
        setPreviewDataUrl(canvas.toDataURL('image/png'));
        // Frozen frame captured — release the live camera immediately so the device light goes off.
        stopStream();
        streamRef.current = null;
        setStream(null);
    }, [stopStream]);

    const handleRetake = useCallback(() => {
        setPreviewDataUrl(null);
        void requestStream();
    }, [requestStream]);

    const handleConfirm = useCallback(() => {
        if (!previewDataUrl) {
            return;
        }
        const base64 = previewDataUrl.split(',')[1] ?? '';
        submitScreenshot({sessionId, participantId}, {stepId: step.id, base64});
    }, [previewDataUrl, submitScreenshot, sessionId, participantId, step.id]);

    return (
        <div className='tw-flex tw-flex-col tw-gap-4'>
            {!stream && !previewDataUrl && !streamError && (
                <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                    <div className='tw-flex-1'>
                        <Button color='primary' disabled={starting} loading={starting} onClick={requestStream}>
                            {t(T_KEYS.SCREENSHOT_START)}
                        </Button>
                    </div>
                    {skipButton}
                </div>
            )}
            {streamError && (
                <div className='tw-flex tw-flex-col tw-gap-2'>
                    <p className='tw-text-sm tw-text-alert tw-m-0'>{streamError}</p>
                    <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                        <div className='tw-flex-1'>
                            <Button color='primary' disabled={starting} loading={starting} onClick={requestStream}>
                                {t(T_KEYS.SCREENSHOT_RETRY)}
                            </Button>
                        </div>
                        {skipButton}
                    </div>
                </div>
            )}
            {stream && !previewDataUrl && (
                <div className='tw-flex tw-flex-col tw-gap-3'>
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        className='tw-w-full tw-rounded tw-bg-dark'
                    />
                    <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                        <div className='tw-flex-1'>
                            <Button color='primary' onClick={handleCapture}>
                                {t(T_KEYS.SCREENSHOT_CAPTURE)}
                            </Button>
                        </div>
                        {skipButton}
                    </div>
                </div>
            )}
            {previewDataUrl && (
                <div className='tw-flex tw-flex-col tw-gap-3'>
                    <img
                        src={previewDataUrl}
                        alt='preview'
                        className='tw-w-full tw-rounded tw-bg-dark'
                    />
                    <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                        <div className='tw-flex tw-flex-1 tw-flex-wrap tw-items-center tw-gap-3'>
                            <Button color='default' icon='refresh' disabled={submitting} onClick={handleRetake}>
                                {t(T_KEYS.SCREENSHOT_RETAKE)}
                            </Button>
                            <Button
                                color='primary'
                                disabled={submitting}
                                loading={submitting}
                                onClick={handleConfirm}
                            >
                                {t(T_KEYS.SCREENSHOT_CONFIRM)}
                            </Button>
                        </div>
                        {skipButton}
                    </div>
                </div>
            )}
            <canvas ref={canvasRef} className='tw-hidden' />
        </div>
    );
};
